/*
 * wallace-drawer.js
 * ------------------------------------------------------------
 * Generic right-hand slide-in panel. Behaviour modelled on
 * wallace-look-drawer.js (scrim, focus trap, focus restore, shared body
 * scroll lock) but content-agnostic and namespaced wallace-drawer__.
 *
 * Panels:  <div data-wallace-drawer="ID" data-heading="Heading" data-eyebrow="Brand" hidden> … </div>
 *          Heading may instead come from a child [data-wallace-drawer-heading].
 *          The heading is an <h2> referenced by the dialog's aria-labelledby.
 *          data-eyebrow (optional) renders a <p> above it.
 *          On init the element's children are moved into the panel body, the
 *          scrim / header / close button are built around them, and the whole
 *          element is portaled to <body>.
 * Openers: any [data-wallace-drawer-open="ID"] (delegated, so they may be
 *          re-rendered). Anything inside a panel with [data-wallace-drawer-close]
 *          closes it.
 * API:     window.WallaceDrawer.open(id) / .close(id)
 * Events:  'wallace-drawer:open' / 'wallace-drawer:close' on the panel element
 *          (bubbling).
 * ------------------------------------------------------------
 */
(function () {
  if (window.WallaceDrawer) return;

  var HIDE_DELAY = 350;
  var drawers = {};
  var active = null;
  var uid = 0;

  function slice(list) {
    return Array.prototype.slice.call(list);
  }

  function fire(el, name) {
    el.dispatchEvent(new CustomEvent(name, { bubbles: true }));
  }

  function removeStale(id, keep) {
    slice(document.querySelectorAll('[data-wallace-drawer-ready]')).forEach(function (node) {
      if (node !== keep && node.getAttribute('data-wallace-drawer') === id && node.parentNode) {
        node.parentNode.removeChild(node);
      }
    });
  }

  function build(el) {
    var id = el.getAttribute('data-wallace-drawer');
    if (!id || el.hasAttribute('data-wallace-drawer-ready')) return;

    // Theme-editor section reloads leave an orphaned, portaled copy behind.
    removeStale(id, el);

    var slot = el.querySelector('[data-wallace-drawer-heading]');
    var headingText = slot ? slot.textContent.trim() : el.getAttribute('data-heading') || '';
    if (slot && slot.parentNode) slot.parentNode.removeChild(slot);

    var content = slice(el.childNodes);
    uid += 1;
    var headingId = 'WallaceDrawerHeading-' + uid;

    var scrim = document.createElement('div');
    scrim.className = 'wallace-drawer__scrim';
    scrim.setAttribute('data-wallace-drawer-close', '');

    var panel = document.createElement('aside');
    panel.className = 'wallace-drawer__panel';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    panel.setAttribute('tabindex', '-1');
    if (headingText) panel.setAttribute('aria-labelledby', headingId);

    var header = document.createElement('div');
    header.className = 'wallace-drawer__header';

    var heading = document.createElement('h2');
    heading.className = 'wallace-drawer__heading';
    heading.id = headingId;
    heading.textContent = headingText;

    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'wallace-drawer__close';
    close.setAttribute('data-wallace-drawer-close', '');
    close.setAttribute('aria-label', 'Close');
    close.innerHTML = '&times;';

    // Optional brand/eyebrow line: a <p> above the heading, never a heading.
    var eyebrowText = el.getAttribute('data-eyebrow') || '';
    var titles = document.createElement('div');
    titles.className = 'wallace-drawer__titles';
    if (eyebrowText) {
      var eyebrow = document.createElement('p');
      eyebrow.className = 'wallace-drawer__eyebrow';
      eyebrow.textContent = eyebrowText;
      titles.appendChild(eyebrow);
    }
    titles.appendChild(heading);

    header.appendChild(titles);
    header.appendChild(close);

    var body = document.createElement('div');
    body.className = 'wallace-drawer__body';
    content.forEach(function (node) {
      body.appendChild(node);
    });

    panel.appendChild(header);
    panel.appendChild(body);
    el.appendChild(scrim);
    el.appendChild(panel);
    el.classList.add('wallace-drawer');
    el.setAttribute('data-wallace-drawer-ready', '');

    // Portal to <body> so position: fixed resolves against the viewport and
    // the z-index is root-level whatever ancestor the markup was rendered in.
    document.body.appendChild(el);

    var controller = {
      id: id,
      el: el,
      panel: panel,
      isOpen: false,
      lastFocused: null,
      hideTimer: null,
    };

    function getFocusable() {
      var selector = 'a[href], button:not([disabled]), select, input, textarea, [tabindex]:not([tabindex="-1"])';
      return slice(panel.querySelectorAll(selector)).filter(function (node) {
        return node.offsetParent !== null;
      });
    }

    controller.onKeydown = function (event) {
      if (event.key === 'Escape') {
        closeDrawer(id);
        return;
      }
      if (event.key !== 'Tab') return;
      var focusable = getFocusable();
      if (focusable.length === 0) {
        event.preventDefault();
        panel.focus();
        return;
      }
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    slice(el.querySelectorAll('[data-wallace-drawer-close]')).forEach(function (node) {
      node.addEventListener('click', function () {
        closeDrawer(id);
      });
    });

    controller.getFocusable = getFocusable;
    drawers[id] = controller;
  }

  function openDrawer(id) {
    var c = drawers[id];
    if (!c || c.isOpen) return;
    if (active && active !== c) closeDrawer(active.id);

    if (c.hideTimer) {
      clearTimeout(c.hideTimer);
      c.hideTimer = null;
    }
    c.lastFocused = document.activeElement;
    c.el.hidden = false;
    // Force reflow so the transform transition runs from the closed state.
    void c.el.offsetWidth;
    c.el.classList.add('is-open');
    c.isOpen = true;
    active = c;
    document.body.classList.add('overflow-hidden');
    document.addEventListener('keydown', c.onKeydown);
    var focusable = c.getFocusable();
    (focusable[0] || c.panel).focus();
    fire(c.el, 'wallace-drawer:open');
  }

  function closeDrawer(id) {
    var c = drawers[id];
    if (!c || !c.isOpen) return;
    c.isOpen = false;
    if (active === c) active = null;
    c.el.classList.remove('is-open');
    document.removeEventListener('keydown', c.onKeydown);
    // Only release the shared scroll lock if no other drawer is open.
    if (!document.querySelector('side-menu.active, cart-drawer.active') && !active) {
      document.body.classList.remove('overflow-hidden');
    }
    c.hideTimer = setTimeout(function () {
      c.el.hidden = true;
      c.hideTimer = null;
    }, HIDE_DELAY);
    if (c.lastFocused && typeof c.lastFocused.focus === 'function') c.lastFocused.focus();
    fire(c.el, 'wallace-drawer:close');
  }

  function init() {
    slice(document.querySelectorAll('[data-wallace-drawer]:not([data-wallace-drawer-ready])')).forEach(build);
  }

  document.addEventListener('click', function (event) {
    var opener = event.target && event.target.closest ? event.target.closest('[data-wallace-drawer-open]') : null;
    if (!opener) return;
    var id = opener.getAttribute('data-wallace-drawer-open');
    if (!drawers[id]) return;
    event.preventDefault();
    openDrawer(id);
  });

  document.addEventListener('shopify:section:load', init);

  window.WallaceDrawer = { open: openDrawer, close: closeDrawer, init: init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
